import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Repeat, Info, Clock, Layers, Link2, Loader2, Upload, GripVertical, Pin, Download, Scissors } from 'lucide-react';
import { useAudio } from '../contexts/AudioContext';
import { useAuth } from '../services/AuthContext';
import { sessionService } from '../services/sessionService';
import { connectStream, startNarrativeGenerationSession } from '../services/agentService';

// TypeScript interfaces for the data schema
interface TimeSpan {
  start: number;      // Start time in minutes
  end: number;        // End time in minutes
  type: 'introduce' | 'deep-dive' | 'reference' | 'backref' | 'continuation' | 'context' | 'summary';
  linkedTo?: number;  // Index of span this references (for backrefs)
  backrefDescription?: string; // Brief description of what is being referenced and why
  importance?: 'minor' | 'moderate' | 'crucial'; // Importance for narrative continuity
}

interface TopicData {
  spans: TimeSpan[];
}

interface ThemeSpan {
  start: number;
  end: number;
  intensity: 1 | 2 | 3;  // 1=tangential, 2=significant, 3=core
  label: string;
}

interface Theme {
  color: string;
  spans: ThemeSpan[];
  topics: Record<string, TopicData>;
}

interface NarrativeStructure {
  [themeName: string]: Theme;
}

interface NarrativeTimelineProps {
  sessionId: string;
  transcript: string;
  ttml: string;
  maxDuration: number;  // Total duration in minutes
  title?: string;
  className?: string;
  shareToken?: string;
  model?: string;
  onModelChange?: (model: string) => void;
  supportedModels?: string[];
  youtubeUrl?: string | null;
}

// Prompt template for LLM data extraction
export const NARRATIVE_EXTRACTION_PROMPT = `
Extract key narrative themes from the transcript and TTML. 

CRITICAL: Return ONLY valid JSON. No markdown, no explanations. Start with { and end with }

IMPORTANT: Use the [HH:MM:SS] timestamps in the transcript to determine exact times. Convert to decimal minutes (e.g., [00:01:30] = 1.5 minutes, [00:02:45] = 2.75 minutes).

REQUIRED JSON STRUCTURE - Follow this EXACTLY:
{
  "Theme Name": {
    "color": "#hexcolor",
    "spans": [...],
    "topics": {
      "Topic Name": {
        "spans": [...]
      }
    }
  }
}

CRITICAL STRUCTURE RULES:
- The root object contains theme names as keys
- Each theme has EXACTLY three properties: "color", "spans", and "topics"
- "topics" is an OBJECT (uses {} not []) containing topic names as keys
- Each topic has EXACTLY one property: "spans" which is an ARRAY
- Double-check: "topics" must be closed with } not ]

Example structure (note overlapping themes):
{
  "AI Safety": {
    "color": "#ef4444",
    "spans": [
      {"start": 0.5, "end": 8.0, "intensity": 3, "label": "Core safety discussion"},
      {"start": 12.0, "end": 15.0, "intensity": 2, "label": "Safety revisited"}
    ],
    "topics": {
      "Alignment": {
        "spans": [
          {"start": 0.5, "end": 1.0, "type": "introduce"},
          {"start": 3.0, "end": 3.5, "type": "deep-dive"}
        ]
      },
      "Red Lines": {
        "spans": [
          {"start": 1.5, "end": 2.0, "type": "deep-dive"}
        ]
      }
    }
  },
  "Technical Implementation": {
    "color": "#3b82f6",
    "spans": [
      {"start": 2.0, "end": 10.0, "intensity": 2, "label": "Implementation details"}
    ],
    "topics": {
      "Architecture": {
        "spans": [
          {"start": 2.0, "end": 3.0, "type": "introduce"}
        ]
      }
    }
  }
}

Field specifications:
- start/end: decimal minutes (e.g., 1.5 = 90 seconds)
- intensity: 1, 2, or 3 (1=brief, 2=significant, 3=core)
- label: 3-5 words maximum
- type: "introduce", "deep-dive", "reference", "backref", "continuation", "context", or "summary"
- Optional fields for backrefs: linkedTo (number), backrefDescription (string), importance ("minor", "moderate", or "crucial")

Requirements:
1. 5-7 major themes for comprehensive coverage
2. 3-6 topics per theme (key points only)
3. Use colors: #f59e0b, #3b82f6, #10b981, #8b5cf6, #ef4444, #6366f1, #ec4899
4. Ensure all JSON brackets and braces are properly matched

COVERAGE RULES:
- THEMES: Should have BROAD, GENEROUS time spans that overlap extensively
- THEMES: Aim for 80%+ coverage of the total timeline across all themes combined
- THEMES: It's GOOD when multiple themes are active simultaneously (overlapping)
- THEMES: Use the TTML segment boundaries as anchor points for theme transitions
- TOPICS: Within each topic, spans should NEVER overlap - they must be sequential
- TOPICS: Align topic spans with TTML segment timestamps when relevant
- TOPICS: Use precise start/end times that correspond to actual discussion points
`;

// Helper function to parse TTML topics
const parseTTMLTopics = (ttmlContent: string) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(ttmlContent, 'application/xml');
    
    let summaryParagraphs = xmlDoc.querySelectorAll('div[region="r_summary"] p');
    if (summaryParagraphs.length === 0) {
      const summaryDiv = xmlDoc.querySelector('div[xml\\:id="d_summary"], div[id="d_summary"]');
      if (summaryDiv) {
        summaryParagraphs = summaryDiv.querySelectorAll('p');
      }
    }

    const topics: Array<{
      start: number; // in minutes
      end: number;
      segmentNumber: number;
      rank: number;
      total: number;
      title: string;
    }> = [];

    summaryParagraphs.forEach((p, index) => {
      const begin = p.getAttribute('begin') || '';
      const end = p.getAttribute('end') || '';
      const htmlContent = p.innerHTML;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const decodedContent = tempDiv.textContent || '';
      
      // Supports [1: 20/20] and [1: ?/9] formats (? = unrated position)
      const segmentMatch = htmlContent.match(/\[(\d+):\s*(\d+|\?)\/(\d+)\]\s*(?:&amp;gt;&amp;gt;|&gt;&gt;|>>)\s*\[([^\]]+)\]/) ||
                          decodedContent.match(/\[(\d+):\s*(\d+|\?)\/(\d+)\]\s*>>\s*\[([^\]]+)\]/);

      // Fallback: match [N/Total] >> [Title] format (legacy/broken format without colon)
      const fallbackMatch = !segmentMatch ? (
        htmlContent.match(/\[(\d+)\/(\d+)\]\s*(?:&amp;gt;&amp;gt;|&gt;&gt;|>>)\s*\[([^\]]+)\]/) ||
        decodedContent.match(/\[(\d+)\/(\d+)\]\s*>>\s*\[([^\]]+)\]/)
      ) : null;

      if (segmentMatch || fallbackMatch) {
        const match = segmentMatch || fallbackMatch;
        const segmentNumber = segmentMatch ? parseInt(segmentMatch[1], 10) : (index + 1);
        const positionRank = segmentMatch ? (parseInt(segmentMatch[2], 10) || 0) : parseInt(fallbackMatch![1], 10);
        const positionTotal = segmentMatch ? (parseInt(segmentMatch[3], 10) || 0) : parseInt(fallbackMatch![2], 10);
        const title = segmentMatch ? segmentMatch[4].trim() : fallbackMatch![3].trim();
        
        // Look for actual score
        const scoreMatch = decodedContent.match(/Score:\s*\[(\d+)\/(\d+)\]/) || 
                          decodedContent.match(/Score:\s*(\d+)\/(\d+)/);
        
        const rank = scoreMatch ? parseInt(scoreMatch[1], 10) : 5;
        const total = scoreMatch ? parseInt(scoreMatch[2], 10) : 10;
        
        // Convert time to minutes
        const timeToMinutes = (timeStr: string) => {
          const parts = timeStr.split(/[:.]/);
          if (parts.length < 3) return 0;
          return parseInt(parts[0]) * 60 + parseInt(parts[1]) + parseInt(parts[2]) / 60;
        };
        
        topics.push({
          start: timeToMinutes(begin),
          end: timeToMinutes(end),
          segmentNumber,
          rank,
          total,
          title
        });
      }
    });

    return topics.sort((a, b) => a.start - b.start);
  } catch (error) {
    console.error('Error parsing TTML topics:', error);
    return [];
  }
};

const getNarrativeMaxEndTime = (narrativeData: any): number => {
  if (!narrativeData || typeof narrativeData !== 'object') {
    return 0;
  }

  let maxEnd = 0;

  Object.values(narrativeData).forEach((theme: any) => {
    if (!theme || typeof theme !== 'object') {
      return;
    }

    const themeSpans = Array.isArray(theme.spans) ? theme.spans : [];
    themeSpans.forEach((span: any) => {
      if (typeof span?.end === 'number') {
        maxEnd = Math.max(maxEnd, span.end);
      }
    });

    const topics = theme.topics && typeof theme.topics === 'object' ? Object.values(theme.topics) : [];
    topics.forEach((topic: any) => {
      const topicSpans = Array.isArray(topic?.spans) ? topic.spans : [];
      topicSpans.forEach((span: any) => {
        if (typeof span?.end === 'number') {
          maxEnd = Math.max(maxEnd, span.end);
        }
      });
    });
  });

  return maxEnd;
};

const normalizeNarrativeData = (
  narrativeData: any,
  maxDurationMinutes: number
): NarrativeStructure | null => {
  if (!narrativeData || typeof narrativeData !== 'object') {
    return null;
  }

  const maxNarrativeEnd = getNarrativeMaxEndTime(narrativeData);
  const looksLikeSeconds = maxDurationMinutes > 0
    ? maxNarrativeEnd > maxDurationMinutes * 1.5
    : maxNarrativeEnd > 360;
  const timeScale = looksLikeSeconds ? 1 / 60 : 1;

  return Object.fromEntries(
    Object.entries(narrativeData).map(([themeName, theme]: [string, any]) => {
      const themeSpans = Array.isArray(theme?.spans) ? theme.spans : [];
      const themeTopics = theme?.topics && typeof theme.topics === 'object' ? theme.topics : {};

      return [
        themeName,
        {
          color: theme?.color || '#6366f1',
          spans: themeSpans.map((span: any) => ({
            ...span,
            start: typeof span?.start === 'number' ? span.start * timeScale : span?.start,
            end: typeof span?.end === 'number' ? span.end * timeScale : span?.end,
          })),
          topics: Object.fromEntries(
            Object.entries(themeTopics).map(([topicName, topic]: [string, any]) => [
              topicName,
              {
                spans: (Array.isArray(topic?.spans) ? topic.spans : []).map((span: any) => ({
                  ...span,
                  start: typeof span?.start === 'number' ? span.start * timeScale : span?.start,
                  end: typeof span?.end === 'number' ? span.end * timeScale : span?.end,
                })),
              },
            ])
          ),
        },
      ];
    })
  ) as NarrativeStructure;
};

const normalizeNarrativeGenerationStatus = (
  status?: string | null
): 'idle' | 'generating' | 'completed' | 'error' => {
  switch ((status || '').toLowerCase()) {
    case 'pending':
    case 'queued':
    case 'running':
    case 'generating':
      return 'generating';
    case 'completed':
    case 'complete':
      return 'completed';
    case 'failed':
    case 'error':
      return 'error';
    default:
      return 'idle';
  }
};

const NarrativeTimeline: React.FC<NarrativeTimelineProps> = ({
  sessionId,
  transcript,
  ttml,
  maxDuration,
  title = "Narrative Timeline",
  className = "",
  shareToken,
  model = '',
  onModelChange,
  supportedModels = [],
  youtubeUrl
}) => {
  // Debug: Log props to understand why button might be disabled
  console.log('[NarrativeTimeline] Props:', {
    sessionId,
    hasTranscript: !!transcript,
    transcriptLength: transcript?.length || 0,
    hasTtml: !!ttml,
    ttmlLength: ttml?.length || 0,
    maxDuration,
    shareToken: !!shareToken
  });
  const [expandedThemes, setExpandedThemes] = useState<Record<number, boolean>>({});
  const [zoomLevel, setZoomLevel] = useState<'themes' | 'topics' | 'details'>('topics');
  const [data, setData] = useState<NarrativeStructure | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generationStatus, setGenerationStatus] = useState<'idle' | 'generating' | 'completed' | 'error'>('idle');
  const [agentProgress, setAgentProgress] = useState<string>('');
  const [versions, setVersions] = useState<Array<{
    id: string;
    version_number: number;
    created_at: string;
    created_by: string;
    is_active: number;
    generation_prompt?: string;
  }>>([]);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [playheadPosition, setPlayheadPosition] = useState(0);
  const [leftColumnX, setLeftColumnX] = useState(20);  // X position of floating left column
  const [isDragging, setIsDragging] = useState(false);
  const [isPinnedLeft, setIsPinnedLeft] = useState(true);
  const [isPinnedRight, setIsPinnedRight] = useState(false);
  const dragStartX = useRef(0);
  const columnStartX = useRef(0);
  const leftColumnRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const agentEventSourceRef = useRef<EventSource | null>(null);
  const { currentTime, seekTo, duration } = useAudio();
  const { token } = useAuth();
  const isEditedMode = false;
  const hasEditCuts = false;
  const timelineMapper: any = null;
  const canSelectModel = Boolean(onModelChange && supportedModels.length > 0);
  const canShowGenerationSettings = !shareToken && canSelectModel;

  // Parse TTML topics
  const ttmlTopics = useMemo(() => parseTTMLTopics(ttml), [ttml]);

  // Filter topics based on edit mode - hide topics fully in cut regions AND remap times
  const editAwareTtmlTopics = useMemo(() => {
    if (!isEditedMode || !timelineMapper || !hasEditCuts) {
      return ttmlTopics;
    }
    return ttmlTopics
      .filter(topic => {
        // Convert minutes to seconds for TimelineMapper
        const startSeconds = topic.start * 60;
        const endSeconds = topic.end * 60;
        // Keep topic if it's not fully in a cut region
        return !timelineMapper.isFullyInCutRegion(startSeconds, endSeconds);
      })
      .map(topic => {
        // Remap times to edited timeline (convert minutes to seconds, map, convert back)
        const startSeconds = topic.start * 60;
        const endSeconds = topic.end * 60;
        let editedStartSeconds = timelineMapper.originalToEdited(startSeconds);
        const editedEndSeconds = timelineMapper.originalToEdited(endSeconds);

        // If start is in a cut region but end is not, use the cut's end time as the effective start
        if (editedStartSeconds === null && editedEndSeconds !== null) {
          const cut = timelineMapper.getCutAt(startSeconds);
          if (cut) {
            editedStartSeconds = timelineMapper.originalToEdited(cut.endTime);
          }
        }

        const effectiveStart = editedStartSeconds !== null ? editedStartSeconds / 60 : topic.start;

        return {
          ...topic,
          // Store original times for seeking
          _originalStart: topic.start,
          _originalEnd: topic.end,
          // Store edited start for sorting
          _editedStart: effectiveStart,
          // Remap to edited times (in minutes to match existing format)
          start: effectiveStart,
          end: editedEndSeconds !== null ? editedEndSeconds / 60 : topic.end
        };
      })
      // Sort by edited start time to maintain chronological order after remapping
      .sort((a, b) => (a as any)._editedStart - (b as any)._editedStart);
  }, [ttmlTopics, isEditedMode, timelineMapper, hasEditCuts]);

  // Get the effective duration for the timeline (edited or original)
  const effectiveDuration = useMemo(() => {
    if (isEditedMode && hasEditCuts && timelineMapper) {
      return timelineMapper.getStats().editedDuration;
    }
    return duration > 0 ? duration : (maxDuration * 60);
  }, [isEditedMode, hasEditCuts, timelineMapper, duration, maxDuration]);

  // Helper to check if a time span (in minutes) is in a cut region
  const isSpanInCutRegion = (startMinutes: number, endMinutes: number): boolean => {
    if (!timelineMapper || !hasEditCuts) return false;
    const startSeconds = startMinutes * 60;
    const endSeconds = endMinutes * 60;
    return timelineMapper.isFullyInCutRegion(startSeconds, endSeconds);
  };

  // Helper to remap a time (in minutes) to edited timeline
  const remapTimeToEdited = (timeMinutes: number): number => {
    if (!isEditedMode || !timelineMapper || !hasEditCuts) return timeMinutes;
    const timeSeconds = timeMinutes * 60;
    const editedSeconds = timelineMapper.originalToEdited(timeSeconds);
    return editedSeconds !== null ? editedSeconds / 60 : timeMinutes;
  };

  // Create edit-aware narrative data with remapped times
  const editAwareData = useMemo(() => {
    if (!data) return null;
    if (!isEditedMode || !timelineMapper || !hasEditCuts) return data;

    // Deep clone and remap all spans
    const remappedData: NarrativeStructure = {};

    Object.entries(data).forEach(([themeName, theme]) => {
      // Filter and remap theme spans
      const filteredThemeSpans = theme.spans
        .filter(span => !isSpanInCutRegion(span.start, span.end))
        .map(span => ({
          ...span,
          start: remapTimeToEdited(span.start),
          end: remapTimeToEdited(span.end)
        }));

      // Filter and remap topic spans
      const remappedTopics: Record<string, TopicData> = {};
      Object.entries(theme.topics).forEach(([topicName, topicData]) => {
        const filteredTopicSpans = topicData.spans
          .filter(span => !isSpanInCutRegion(span.start, span.end))
          .map(span => ({
            ...span,
            start: remapTimeToEdited(span.start),
            end: remapTimeToEdited(span.end)
          }));

        if (filteredTopicSpans.length > 0) {
          remappedTopics[topicName] = { spans: filteredTopicSpans };
        }
      });

      // Only include theme if it has any spans or topics remaining
      if (filteredThemeSpans.length > 0 || Object.keys(remappedTopics).length > 0) {
        remappedData[themeName] = {
          color: theme.color,
          spans: filteredThemeSpans,
          topics: remappedTopics
        };
      }
    });

    return remappedData;
  }, [data, isEditedMode, timelineMapper, hasEditCuts]);

  // Update playhead position
  useEffect(() => {
    if (duration > 0) {
      const percentage = (currentTime / duration) * 100;
      setPlayheadPosition(percentage);
    }
  }, [currentTime, duration]);

  // Initialize expanded state when data loads
  useEffect(() => {
    if (data) {
      const initialExpanded: Record<number, boolean> = {};
      Object.keys(data).forEach((_, idx) => {
        initialExpanded[idx] = true;
      });
      setExpandedThemes(initialExpanded);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      agentEventSourceRef.current?.close();
      agentEventSourceRef.current = null;
    };
  }, []);

  // Load cached data and versions on mount
  useEffect(() => {
    // Skip API calls if sessionId is empty (happens during session creation)
    if (!sessionId) {
      return;
    }
    loadNarrativeData();
    loadNarrativeVersions();
    void syncNarrativeGenerationStatus();
  }, [sessionId, shareToken, token]);

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    const handleSessionRefresh = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (detail.sessionId && detail.sessionId !== sessionId) {
        return;
      }
      void loadNarrativeData();
      void loadNarrativeVersions();
    };

    const handleSessionInvalidated = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (detail.sessionId !== sessionId) {
        return;
      }
      const scopes = Array.isArray(detail.scopes) ? detail.scopes : [];
      if (scopes.length === 0 || scopes.includes('narrative') || scopes.includes('ttml') || scopes.includes('session')) {
        void loadNarrativeData();
        void loadNarrativeVersions();
      }
    };

    window.addEventListener('rescript-session-refresh-needed', handleSessionRefresh as EventListener);
    window.addEventListener('rescript-session-invalidated', handleSessionInvalidated as EventListener);

    return () => {
      window.removeEventListener('rescript-session-refresh-needed', handleSessionRefresh as EventListener);
      window.removeEventListener('rescript-session-invalidated', handleSessionInvalidated as EventListener);
    };
  }, [sessionId, shareToken, token]);

  const loadNarrativeData = async () => {
    if (!sessionId) return; // Guard against empty sessionId
    try {
      const cachedData = await sessionService.getNarrativeData(sessionId, shareToken || token || undefined);
      if (cachedData) {
        setData(normalizeNarrativeData(cachedData, maxDuration));
      }
    } catch (err) {
      console.error('Error loading cached narrative data:', err);
    }
  };

  const loadNarrativeVersions = async () => {
    if (!sessionId) return; // Guard against empty sessionId
    if (shareToken) {
      setVersions([]);
      setActiveVersionId(null);
      return;
    }
    try {
      const versionsData = await sessionService.getNarrativeVersions(sessionId, token || undefined);
      if (versionsData && versionsData.versions) {
        setVersions(versionsData.versions);
        // Set the active version
        const activeVersion = versionsData.versions.find(v => v.is_active === 1);
        if (activeVersion) {
          setActiveVersionId(activeVersion.id);
        }
      }
    } catch (err) {
      console.error('Error loading narrative versions:', err);
    }
  };

  const syncNarrativeGenerationStatus = async () => {
    if (!sessionId) return;

    try {
      const statusData = await sessionService.getNarrativeGenerationStatus(sessionId, shareToken || token || undefined);
      const normalizedStatus = normalizeNarrativeGenerationStatus(statusData.status);

      setGenerationStatus(normalizedStatus);

      if (normalizedStatus === 'generating') {
        setError(null);
        if (!agentEventSourceRef.current) {
          setAgentProgress(prev => prev || 'Topic map generation is running on the server...');
        }
        return;
      }

      setAgentProgress('');

      if (normalizedStatus === 'error') {
        setError(statusData.error || 'Topic map generation failed');
        return;
      }

      setError(null);

      if (normalizedStatus === 'completed') {
        void loadNarrativeData();
        void loadNarrativeVersions();
      }
    } catch (err) {
      console.error('Error checking topic map generation status:', err);
    }
  };

  useEffect(() => {
    if (!sessionId || generationStatus !== 'generating' || agentEventSourceRef.current) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void syncNarrativeGenerationStatus();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [generationStatus, sessionId, shareToken, token]);

  const switchToVersion = async (versionId: string) => {
    try {
      setLoading(true);
      await sessionService.activateNarrativeVersion(sessionId, versionId, token || undefined);
      // Reload data and versions
      await loadNarrativeData();
      await loadNarrativeVersions();
      setLoading(false);
    } catch (err) {
      console.error('Error switching narrative version:', err);
      setError('Failed to switch version');
      setLoading(false);
    }
  };
  
  const generateNarrativeData = async () => {
    setError(null);

    try {
      // Validate inputs
      if (!transcript || transcript.trim().length === 0) {
        throw new Error('Transcript is required to generate a topic map');
      }

      if (!ttml || ttml.trim().length === 0) {
        throw new Error('A metadata revision is required to generate a topic map. Please select a metadata revision in the Ranking and Metadata tab first.');
      }

      console.log('[NARRATIVE] Starting agent-based generation');

      agentEventSourceRef.current?.close();
      agentEventSourceRef.current = null;

      const result = await startNarrativeGenerationSession({
        sessionId,
        model,
      }, token || undefined);
      console.log('[NARRATIVE] Agent generation started:', result);

      if (!result.success) {
        throw new Error('Failed to start topic map generation');
      }

      setGenerationStatus('generating');
      setAgentProgress('Starting agent...');

      const eventSource = connectStream(
        result.agentSessionId,
        (event) => {
          if (event.type === 'text_delta' || event.type === 'text') {
            setAgentProgress(event.content || event.message || 'Working...');
            return;
          }
          if (event.type === 'status') {
            setAgentProgress(event.message || 'Working...');
            return;
          }
          if (event.type === 'tool_start') {
            setAgentProgress(`Calling ${event.toolName || event.tool || 'tool'}...`);
            return;
          }
          if (event.type === 'done' || event.type === 'complete') {
            eventSource.close();
            if (agentEventSourceRef.current === eventSource) {
              agentEventSourceRef.current = null;
            }
            setAgentProgress('');
            setGenerationStatus('completed');
            window.dispatchEvent(new CustomEvent('rescript-session-refresh-needed', {
              detail: { sessionId, scopes: ['narrative'] },
            }));
            void loadNarrativeData();
            void loadNarrativeVersions();
            return;
          }
          if (event.type === 'error') {
            eventSource.close();
            if (agentEventSourceRef.current === eventSource) {
              agentEventSourceRef.current = null;
            }
            setAgentProgress('');
            setGenerationStatus('error');
            setError(event.message || 'Topic map generation failed');
          }
        },
        () => {
          eventSource.close();
          if (agentEventSourceRef.current === eventSource) {
            agentEventSourceRef.current = null;
          }
          setAgentProgress('');
          setGenerationStatus('error');
          setError('Topic map generation stream disconnected');
        },
        token || undefined
      );

      agentEventSourceRef.current = eventSource;
    } catch (err) {
      console.error('Error starting narrative generation:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate topic map');
      setGenerationStatus('error');
      setAgentProgress('');
    }
  };

  // Type colors - single hue with intensity variations
  const typeStyles: Record<string, { bg: string; opacity: number; border?: string }> = {
    context: { bg: '#6366f1', opacity: 0.3 },        // lightest - background/setup
    introduce: { bg: '#6366f1', opacity: 0.4 },      // light - first mention
    reference: { bg: '#6366f1', opacity: 0.5 },      // medium - brief mention
    continuation: { bg: '#6366f1', opacity: 0.6 },   // medium-dark - ongoing
    'deep-dive': { bg: '#6366f1', opacity: 0.8 },    // dark - in-depth
    summary: { bg: '#6366f1', opacity: 0.7 },        // medium-dark - concluding
    backref: { bg: '#ef4444', opacity: 0.7 }        // red for backrefs
  };

  const toggleTheme = (idx: number) => {
    setExpandedThemes(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  // Handle dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isPinnedLeft && !isPinnedRight) {
      setIsDragging(true);
      dragStartX.current = e.clientX;
      columnStartX.current = leftColumnX;
      e.preventDefault();
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const deltaX = e.clientX - dragStartX.current;
      const newX = columnStartX.current + deltaX;
      setLeftColumnX(Math.max(0, Math.min(window.innerWidth - 300, newX)));
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  // Sync scroll between left column and main content
  useEffect(() => {
    const leftColumn = leftColumnRef.current;
    const mainContent = mainContentRef.current;
    
    if (!leftColumn || !mainContent) return;
    
    let isScrolling = false;
    
    const syncScroll = (source: HTMLElement, target: HTMLElement) => {
      if (isScrolling) return;
      isScrolling = true;
      
      const scrollPercentage = source.scrollTop / (source.scrollHeight - source.clientHeight);
      target.scrollTop = scrollPercentage * (target.scrollHeight - target.clientHeight);
      
      setTimeout(() => {
        isScrolling = false;
      }, 10);
    };
    
    const handleLeftScroll = () => syncScroll(leftColumn, mainContent);
    const handleMainScroll = () => syncScroll(mainContent, leftColumn);
    
    leftColumn.addEventListener('scroll', handleLeftScroll);
    mainContent.addEventListener('scroll', handleMainScroll);
    
    return () => {
      leftColumn.removeEventListener('scroll', handleLeftScroll);
      mainContent.removeEventListener('scroll', handleMainScroll);
    };
  }, []);

  // Handle pinning
  const handlePinLeft = () => {
    setIsPinnedLeft(true);
    setIsPinnedRight(false);
    setLeftColumnX(20);
  };

  const handlePinRight = () => {
    setIsPinnedRight(true);
    setIsPinnedLeft(false);
    const timelineWidth = timelineRef.current?.offsetWidth || 800;
    setLeftColumnX(timelineWidth - 280);
  };

  const timeToX = (time: number) => {
    // Convert time from minutes to seconds to match audio duration
    const timeInSeconds = time * 60;
    // Use effectiveDuration which accounts for edited timeline
    return `${(timeInSeconds / effectiveDuration) * 100}%`;
  };

  const handleBarClick = (startTime: number) => {
    // Use the displayed start time (already remapped in edit mode)
    // Convert from minutes to seconds and seek
    const timeInSeconds = startTime * 60;
    seekTo(timeInSeconds);
  };

  // Generate time markers based on effective duration
  const timeMarkers = useMemo(() => {
    const maxMinutes = effectiveDuration / 60;
    const interval = maxMinutes > 120 ? 30 : maxMinutes > 60 ? 15 : maxMinutes > 30 ? 10 : 5;
    const markers: number[] = [];
    for (let t = 0; t <= maxMinutes; t += interval) {
      markers.push(t);
    }
    return markers;
  }, [effectiveDuration]);

  // Convert minutes to TTML time format (HH:MM:SS.mmm)
  const minutesToTTMLTime = (minutes: number): string => {
    const totalSeconds = minutes * 60;
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = Math.floor(totalSeconds % 60);
    const milliseconds = Math.round((totalSeconds % 1) * 1000);
    
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  };

  // Generate TTML from narrative data
  const generateTTML = () => {
    if (!data) return '';

    const themes = Object.entries(data);
    const numThemes = themes.length;
    
    // Calculate region layout - distribute themes vertically
    const regionHeight = Math.floor(90 / (numThemes + 1)); // Leave space for details track
    
    // Collect all topic spans across all themes for the details track
    const allTopicSpans: Array<{
      start: number;
      end: number;
      type: string;
      themeName: string;
      topicName: string;
      themeColor: string;
    }> = [];
    
    themes.forEach(([themeName, theme]) => {
      Object.entries(theme.topics).forEach(([topicName, topic]) => {
        topic.spans.forEach(span => {
          allTopicSpans.push({
            ...span,
            themeName,
            topicName,
            themeColor: theme.color
          });
        });
      });
    });
    
    // Sort spans by start time, then by priority (deep-dive > others)
    const typePriority: Record<string, number> = {
      'deep-dive': 5,
      'summary': 4,
      'continuation': 3,
      'reference': 2,
      'introduce': 1,
      'context': 0,
      'backref': 0
    };
    
    allTopicSpans.sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      return (typePriority[b.type] || 0) - (typePriority[a.type] || 0);
    });
    
    // Remove overlaps, preferring higher priority spans
    const nonOverlappingSpans: typeof allTopicSpans = [];
    allTopicSpans.forEach(span => {
      const hasOverlap = nonOverlappingSpans.some(existing => 
        (span.start >= existing.start && span.start < existing.end) ||
        (span.end > existing.start && span.end <= existing.end) ||
        (span.start <= existing.start && span.end >= existing.end)
      );
      if (!hasOverlap) {
        nonOverlappingSpans.push(span);
      }
    });

    const ttml = `<?xml version="1.0" encoding="UTF-8"?>
<tt xml:lang="en" xmlns="http://www.w3.org/ns/ttml" xmlns:ttm="http://www.w3.org/ns/ttml#metadata" xmlns:tts="http://www.w3.org/ns/ttml#styling" xmlns:ttp="http://www.w3.org/ns/ttml#parameter" xmlns:ittp="http://www.w3.org/ns/ttml/profile/imsc1#parameter" xmlns:itts="http://www.w3.org/ns/ttml/profile/imsc1#styling" ttp:profile="http://www.w3.org/ns/ttml/profile/imsc1/text" ttp:frameRate="24" ttp:frameRateMultiplier="1000 1001" ttp:dropMode="nonDrop" ttp:timeBase="media">
  <head>
    <styling>
      ${themes.map(([themeName], idx) => 
        `<style xml:id="style_theme_${idx}" tts:color="#ffffff" tts:opacity="1" tts:fontSize="100%" tts:fontFamily="proportionalSansSerif" tts:textAlign="center" tts:backgroundColor="${themes[idx][1].color}" tts:showBackground="whenActive"/>`
      ).join('\n      ')}
      <style xml:id="style_details" tts:color="#ffffff" tts:opacity="1" tts:fontSize="90%" tts:fontFamily="proportionalSansSerif" tts:fontWeight="bold" tts:textOutline="#000000 2px" tts:textAlign="start"/>
    </styling>
    <layout>
      ${themes.map(([themeName], idx) => 
        `<region xml:id="r_theme_${idx}" tts:origin="5% ${5 + idx * regionHeight}%" tts:extent="90% ${regionHeight - 2}%" tts:displayAlign="center" tts:backgroundColor="#000000" tts:opacity="0.60" tts:showBackground="whenActive"/>`
      ).join('\n      ')}
      <region xml:id="r_details" tts:origin="5% ${5 + numThemes * regionHeight}%" tts:extent="90% ${regionHeight - 2}%" tts:displayAlign="before" tts:backgroundColor="#000000" tts:opacity="0.70" tts:showBackground="whenActive"/>
    </layout>
  </head>
  <body>
    ${themes.map(([themeName, theme], idx) => `
    <div xml:id="d_theme_${idx}" region="r_theme_${idx}" style="style_theme_${idx}">
      ${theme.spans.map((span, spanIdx) => 
        `<p begin="${minutesToTTMLTime(span.start)}" end="${minutesToTTMLTime(span.end)}">${themeName}<br/>${span.label || ''}<br/>Intensity: ${span.intensity}/3</p>`
      ).join('\n      ')}
    </div>`).join('\n    ')}
    
    <div xml:id="d_details" region="r_details" style="style_details">
      ${nonOverlappingSpans.map((span, idx) => 
        `<p begin="${minutesToTTMLTime(span.start)}" end="${minutesToTTMLTime(span.end)}">[${span.themeName}] ${span.topicName}<br/>Type: ${span.type}${span.type === 'backref' ? ' (reference)' : ''}</p>`
      ).join('\n      ')}
    </div>
  </body>
</tt>`;

    return ttml;
  };

  const handleDownloadTTML = () => {
    const ttml = generateTTML();
    if (!ttml) return;
    
    const blob = new Blob([ttml], { type: 'application/ttml+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `narrative_timeline_${sessionId}.ttml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Calculate statistics
  const stats = useMemo(() => {
    if (!data) return null;
    
    let mostReferenced = { name: '', count: 0 };
    let longestArc = { name: '', duration: 0 };
    let totalBackrefs = 0;

    Object.entries(data).forEach(([themeName, theme]) => {
      const themeDuration = Math.max(...theme.spans.map(s => s.end)) - Math.min(...theme.spans.map(s => s.start));
      if (themeDuration > longestArc.duration) {
        longestArc = { name: themeName, duration: themeDuration };
      }

      Object.entries(theme.topics).forEach(([topicName, topic]) => {
        const backrefCount = topic.spans.filter(s => s.type === 'backref').length;
        totalBackrefs += backrefCount;
        
        const totalRefs = topic.spans.length;
        if (totalRefs > mostReferenced.count) {
          mostReferenced = { name: topicName, count: totalRefs };
        }
      });
    });

    return { mostReferenced, longestArc, totalBackrefs };
  }, [data]);
  const narrativeCounts = useMemo(() => {
    if (!editAwareData) {
      return { themes: 0, topics: 0 };
    }

    const themes = Object.keys(editAwareData).length;
    const topics = Object.values(editAwareData).reduce((count, theme) => {
      return count + Object.keys(theme.topics || {}).length;
    }, 0);

    return { themes, topics };
  }, [editAwareData]);

  if (!data) {
    return (
      <div className={`bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg p-6 ${className}`}>
        <h2 className="text-2xl font-bold mb-6 text-center">{title}</h2>
        
        {generationStatus === 'generating' ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Generating topic map...</p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">This is running on the server and will continue even if you navigate away</p>
            {agentProgress && (
              <p className="text-xs text-gray-400 dark:text-gray-600 mt-1 max-w-md text-center">
                {agentProgress}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <Upload className="w-12 h-12 text-gray-500 dark:text-gray-500 mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">No topic map available</p>
            {shareToken ? (
              <p className="text-sm text-gray-500 dark:text-gray-500 text-center max-w-md">
                This public share only exposes an existing topic map. Nothing has been generated for this episode yet.
              </p>
            ) : (
              <>
                {/* Model Selector */}
                {canSelectModel && (
                  <div className="mb-4 w-full max-w-xs">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Select AI Model
                    </label>
                    <select
                      value={model}
                      onChange={(e) => onModelChange?.(e.target.value)}
                      className="w-full px-3 py-2 border border-[#cfd5df] dark:border-[#40506a] bg-white dark:bg-[#131a25] text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-[#ffcf74] focus:border-transparent"
                    >
                      {supportedModels.map((modelOption) => (
                        <option key={modelOption} value={modelOption}>
                          {modelOption}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <button
                  onClick={generateNarrativeData}
                  disabled={!transcript || !ttml}
                  className="flex items-center gap-2 px-6 py-3 bg-[#FFB52B] hover:bg-[#f0a61d] text-[#232E3F] disabled:bg-gray-300 dark:disabled:bg-[#2f3c52] disabled:text-gray-500 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Generate Topic Map
                </button>
                {(!transcript || !ttml) && (
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Transcript and metadata revision required</p>
                )}
              </>
            )}
          </div>
        )}
        
        {error && (
          <div className="mt-4 p-4 bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg h-full relative overflow-hidden ${className}`}>

      {/* SVG Pattern Definitions - use editAwareData for consistent indexing */}
      <svg width="0" height="0" className="absolute">
        <defs>
          {editAwareData && Object.entries(editAwareData).map(([theme, themeData], idx) => (
            <pattern
              key={theme}
              id={`stripes-${idx}`}
              patternUnits="userSpaceOnUse"
              width="8"
              height="8"
            >
              <rect width="8" height="8" fill={themeData.color} fillOpacity="0.3" />
              <path d="M0,8 L8,0" stroke={themeData.color} strokeWidth="1" opacity="0.6" />
            </pattern>
          ))}
        </defs>
      </svg>

      <div className="relative overflow-y-auto overflow-x-auto h-full" ref={mainContentRef}>

        {/* Controls */}
        <div className="mb-6 px-6 pt-6">
          {/* Model selector - hide for public shares */}
          {canShowGenerationSettings && (
            <div className="flex justify-center gap-4 mb-4">
              {canSelectModel && (
                <div className="w-full max-w-xs">
                  <select
                    value={model}
                    onChange={(e) => onModelChange?.(e.target.value)}
                    className="w-full px-3 py-2 border border-[#cfd5df] dark:border-[#40506a] bg-white dark:bg-[#131a25] text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-[#ffcf74] focus:border-transparent text-sm"
                  >
                    {supportedModels.map((modelOption) => (
                      <option key={modelOption} value={modelOption}>
                        {modelOption}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {generationStatus === 'generating' && (
            <div className="text-center mb-4">
              <p className="text-sm text-[#8a6115] dark:text-[#ffcf74]">
                Topic map generation is running on the server.
              </p>
              {agentProgress && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {agentProgress}
                </p>
              )}
            </div>
          )}

          {/* View toggle */}
          <div className="flex justify-center gap-2 mb-4">
            <button
              onClick={() => setZoomLevel(zoomLevel === 'themes' ? 'topics' : 'themes')}
              className="px-4 py-2 rounded text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              {zoomLevel === 'themes' ? (
                <>
                  <Clock className="w-4 h-4 inline mr-2" />
                  Show Topics
                </>
              ) : (
                <>
                  <Layers className="w-4 h-4 inline mr-2" />
                  Themes Only
                </>
              )}
            </button>
            <div className="inline-flex items-center rounded-full bg-gray-100 px-3 py-2 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {narrativeCounts.themes} themes · {narrativeCounts.topics} topics
            </div>
            {!shareToken && (
              <button
                onClick={handleDownloadTTML}
                disabled={!data}
                className="ml-2 px-4 py-2 rounded text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4 inline mr-2" />
                Download TTML
              </button>
            )}
            {!shareToken && (
              <button
                onClick={generateNarrativeData}
                disabled={generationStatus === 'generating'}
                className={`ml-2 px-4 py-2 rounded text-sm transition-all flex items-center inline-flex ${
                  generationStatus === 'generating'
                    ? 'bg-[#FFB52B] text-[#232E3F] animate-pulse' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                } disabled:cursor-not-allowed`}
              >
                {generationStatus === 'generating' ? (
                  <>
                    <div className="relative mr-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <div className="absolute inset-0 w-4 h-4 animate-ping">
                        <Loader2 className="w-4 h-4 opacity-30" />
                      </div>
                    </div>
                    Generating topic map...
                  </>
                ) : (
                  <>
                    <Repeat className="w-4 h-4 mr-2" />
                    Regenerate Topic Map
                  </>
                )}
              </button>
            )}
          </div>

          {/* Version selector */}
          {versions.length > 0 && (
            <div className="flex justify-center items-center gap-2 mb-4">
              <div className="flex items-center gap-1">
                {versions.map((version, index) => (
                  <button
                    key={version.id}
                    onClick={() => switchToVersion(version.id)}
                    disabled={loading}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      version.id === activeVersionId
                        ? 'bg-[#FFB52B] w-3 h-3'
                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                    } disabled:opacity-50`}
                    title={`Version ${version.version_number} - ${new Date(version.created_at).toLocaleString()} by ${version.created_by}`}
                  />
                ))}
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                v{versions.find(v => v.id === activeVersionId)?.version_number || '?'}
              </span>
            </div>
          )}

        </div>

        {/* Main content area with timeline */}
        <div className="flex">
          {/* Timeline visualization - full width since column is floating */}
          <div className="w-full overflow-x-hidden">
            <div className="relative px-0" ref={timelineRef}>
              {/* Time axis with TTML topic markers */}
              <div className="h-24 relative border-b border-gray-300 dark:border-gray-700 mb-4">
                {/* Time markers - dynamic based on effective duration */}
                {timeMarkers.map(time => (
                  <div key={time} className="absolute" style={{ left: timeToX(time) }}>
                    <div className="w-px h-3 bg-gray-400 dark:bg-gray-500"></div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 absolute -translate-x-1/2 top-4">
                      {time}m
                    </span>
                  </div>
                ))}
                
                {/* TTML topic vertical lines and labels - edit-aware */}
                {editAwareTtmlTopics.map((topic, idx) => {
                  const score = topic.rank / topic.total;
                  const color = score >= 0.7 ? '#10b981' : score >= 0.4 ? '#f59e0b' : '#ef4444';

                  return (
                    <div
                      key={idx}
                      className="absolute top-8 bottom-0"
                      style={{ left: timeToX(topic.start) }}
                    >
                      {/* Vertical line */}
                      <div
                        className="absolute w-px h-full border-l border-dotted"
                        style={{ borderColor: color, opacity: 0.3 }}
                      />
                      {/* Topic number - preserves original segment number */}
                      <div className="absolute -top-2 -translate-x-1/2 text-xs font-medium" style={{ color }}>
                        {topic.segmentNumber}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Playhead cursor */}
              {duration > 0 && (
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10 pointer-events-none"
                  style={{ left: `${playheadPosition}%` }}
                >
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-red-500"></div>
                </div>
              )}

              {/* Background crosshatch for TTML topics - edit-aware */}
              <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.2 }}>
                {editAwareTtmlTopics.map((topic, idx) => {
                  const width = parseFloat(timeToX(topic.end)) - parseFloat(timeToX(topic.start));
                  const score = topic.rank / topic.total;
                  const color = score >= 0.7 ? '#10b981' : score >= 0.4 ? '#f59e0b' : '#ef4444';

                  return (
                    <div
                      key={idx}
                      className="absolute top-0 bottom-0"
                      style={{
                        left: timeToX(topic.start),
                        width: `${width}%`,
                        background: `repeating-linear-gradient(45deg, transparent, transparent 10px, ${color} 10px, ${color} 11px)`
                      }}
                    />
                  );
                })}
              </div>
              
              {/* Theme/Topic spans - use editAwareData for edit mode */}
              {editAwareData && Object.entries(editAwareData).map(([theme, themeData], themeIdx) => (
                <div key={theme} className="mb-6 relative">
                  {/* Theme name label */}
                  {themeData.spans.length > 0 && (() => {
                    const firstSpan = themeData.spans[0];
                    const startPercent = (firstSpan.start / maxDuration) * 100;
                    const isOverflowing = startPercent > 80; // If starting after 80% of timeline
                    
                    return (
                      <div
                        className="absolute text-sm font-semibold whitespace-nowrap dark:[text-shadow:0_0_3px_rgba(0,0,0,0.8),0_0_6px_rgba(0,0,0,0.5)]"
                        style={{
                          left: isOverflowing ? 'calc(100% - 10px)' : timeToX(firstSpan.start),
                          top: '-20px',
                          color: themeData.color,
                          textAlign: isOverflowing ? 'right' : 'left',
                          transform: isOverflowing ? 'translateX(-100%)' : 'none'
                        }}
                      >
                        {theme}
                      </div>
                    );
                  })()}
                  
                  {/* Theme-level spans - edit-aware (already filtered and remapped in editAwareData) */}
                  <div className="relative mb-2" style={{ height: '48px' }}> {/* Match label height */}
                    {themeData.spans.filter(span =>
                      span &&
                      typeof span.start === 'number' &&
                      typeof span.end === 'number' &&
                      span.start >= 0 &&
                      span.end > span.start
                    ).map((span, idx) => (
                      <div 
                        key={idx} 
                        className="absolute cursor-pointer" 
                        style={{ left: timeToX(span.start), width: `${parseFloat(timeToX(span.end)) - parseFloat(timeToX(span.start))}%` }}
                        onClick={() => handleBarClick(span.start)}
                      >
                        {/* Background bar with pattern */}
                        <svg 
                          className="absolute" 
                          style={{ 
                            width: '100%', 
                            height: '32px',
                            top: '8px',
                            opacity: 0.4 + (span.intensity * 0.3)
                          }}
                        >
                          <rect 
                            width="100%" 
                            height="32" 
                            fill={`url(#stripes-${themeIdx})`}
                            stroke={themeData.color}
                            strokeWidth="2"
                            rx="4"
                            className="hover:opacity-100 transition-opacity"
                          />
                        </svg>
                        {/* Text label above bar with smart overflow */}
                        <div
                          className="absolute text-xs font-medium px-2 py-0.5 rounded shadow-sm"
                          style={{
                            backgroundColor: themeData.color,
                            color: '#ffffff',
                            top: '0',
                            left: '0',
                            whiteSpace: 'nowrap',
                            zIndex: idx === themeData.spans.length - 1 || 
                              (idx < themeData.spans.length - 1 && 
                               span.end + 5 < themeData.spans[idx + 1].start) ? 3 : 1
                          }}
                          title={span.label}
                        >
                          {span.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Topic-level spans */}
                  {zoomLevel !== 'themes' && expandedThemes[themeIdx] && (
                    <div className="relative">
                      {Object.entries(themeData.topics).map(([topicName, topicData]) => (
                        <div key={topicName} className="h-6 relative">
                          {/* Dotted line background for alignment */}
                          <div className="absolute left-6 right-0 h-px border-b border-dotted border-gray-300 dark:border-gray-700" style={{ top: '50%' }}></div>
                          {topicData.spans.filter(span =>
                            span &&
                            typeof span.start === 'number' &&
                            typeof span.end === 'number' &&
                            span.start >= 0 &&
                            span.end > span.start
                          ).map((span, idx) => {
                            const isBackref = span.type === 'backref';
                            // Calculate width using the same duration scale as positioning
                            const startPercent = parseFloat(timeToX(span.start));
                            const endPercent = parseFloat(timeToX(span.end));
                            const spanWidth = endPercent - startPercent;
                            const style = typeStyles[span.type] || { bg: '#9ca3af' };
                            const isLastSpan = idx === topicData.spans.length - 1;
                            
                            return (
                              <div
                                key={idx}
                                className="absolute h-4 rounded transition-opacity hover:opacity-100 cursor-pointer group"
                                style={{
                                  left: timeToX(span.start),
                                  width: `${spanWidth}%`,
                                  backgroundColor: style.bg,
                                  opacity: style.opacity,
                                  top: '0.25rem',
                                  border: style.border || 'none'
                                }}
                                title={(() => {
                                  if (isBackref && span.linkedTo !== undefined && span.linkedTo < topicData.spans.length) {
                                    const linkedSpan = topicData.spans[span.linkedTo];
                                    if (linkedSpan && typeof linkedSpan.start === 'number') {
                                      return `${topicName} (references ${linkedSpan.start.toFixed(1)}m)`;
                                    }
                                  }
                                  return topicName;
                                })()}
                                onClick={() => handleBarClick(span.start)}
                              >
                                {/* Backref indicator */}
                                {isBackref && span.linkedTo !== undefined && span.linkedTo < topicData.spans.length && topicData.spans[span.linkedTo] && typeof topicData.spans[span.linkedTo].start === 'number' && (
                                  <>
                                    <div className="absolute -top-5 left-0 bg-red-600 text-white text-xs px-1 rounded shadow-sm" style={{ fontSize: '10px' }}>
                                      ← {topicData.spans[span.linkedTo].start.toFixed(1)}m
                                    </div>
                                    {/* Connection line on hover */}
                                    <div
                                      className="absolute top-2 h-px bg-red-400 opacity-0 group-hover:opacity-60 transition-opacity pointer-events-none"
                                      style={{
                                        right: '100%',
                                        width: `${Math.abs(parseFloat(timeToX(span.start)) - parseFloat(timeToX(topicData.spans[span.linkedTo].start)))}%`,
                                        transform: topicData.spans[span.linkedTo].start < span.start ? 'none' : 'translateX(-100%)'
                                      }}
                                    />
                                  </>
                                )}
                              </div>
                            );
                          })}
                          
                          {/* Topic name label after last bar */}
                          {topicData.spans.length > 0 && (() => {
                            const lastBar = topicData.spans[topicData.spans.length - 1];
                            const endPercent = parseFloat(timeToX(lastBar.end));
                            const isOverflowing = endPercent > 90; // If ending after 90% of timeline
                            const firstBar = topicData.spans[0];
                            const hasBackref = topicData.spans.some(span => span.type === 'backref');
                            const marginRight = hasBackref ? 35 : 5; // Extra margin if there's a backref badge
                            
                            return (
                              <div
                                className="absolute text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap"
                                style={{
                                  left: isOverflowing ? `calc(${timeToX(firstBar.start)} + 1.5rem - ${marginRight}px)` : `calc(${timeToX(lastBar.end)} + ${marginRight}px + 1.5rem)`,
                                  top: '0.25rem',
                                  lineHeight: '1rem',
                                  textAlign: isOverflowing ? 'right' : 'left',
                                  transform: isOverflowing ? 'translateX(-100%)' : 'none'
                                }}
                              >
                                {topicName}
                              </div>
                            );
                          })()}
                          
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        </div>


        {/* Summary stats */}
        {stats && (
          <div className="mt-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Narrative Insights
            </h3>
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div className="flex items-center gap-2">
                <Link2 className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Most Referenced:</span>
                  <span className="ml-2 font-medium">{stats.mostReferenced.name} ({stats.mostReferenced.count} times)</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Longest Arc:</span>
                  <span className="ml-2 font-medium">{stats.longestArc.name} ({stats.longestArc.duration.toFixed(0)} min)</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Repeat className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Back References:</span>
                  <span className="ml-2 font-medium">{stats.totalBackrefs} connections</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NarrativeTimeline;

// Export types and prompt for external use
export type { NarrativeStructure, Theme, ThemeSpan, TopicData, TimeSpan };
